import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, IconButton,
  Alert, MenuItem
} from '@mui/material';
import { Add, Edit, MailOutline, ContentCopy, CheckCircle, Block, Delete, UploadFile, Description } from '@mui/icons-material';

const AdminSuppliers = () => {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [inviteLoadingId, setInviteLoadingId] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState('');
  const [contractUploading, setContractUploading] = useState(false);
  const [alipayUploading, setAlipayUploading] = useState(false);
  const [wechatQrUploading, setWechatQrUploading] = useState(false);
  const [inviteDialog, setInviteDialog] = useState({ open: false, link: '', email: '' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', supplier: null });
  const [pageMessage, setPageMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    companyName: '', companyNameAr: '', country: '',
    contactName: '', contactPosition: '', contactEmail: '', contactPhone: '',
    wechat: '', whatsapp: '',
    commissionRate: 10, contractFile: '', alipayQrCode: '', wechatQrCode: '',
    bankName: '', accountName: '', accountNumber: '', swiftCode: '', bankCurrency: 'CNY',
    language: 'en'
  });
  const contractInputRef = useRef(null);
  const alipayInputRef = useRef(null);
  const wechatQrInputRef = useRef(null);

  useEffect(() => { loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/suppliers');
      setSuppliers(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      if (selectedSupplier) {
        await api.put(`/suppliers/${selectedSupplier._id}`, formData);
      } else {
        await api.post('/suppliers', formData);
      }
      setPageMessage({
        type: 'success',
        text: selectedSupplier ? 'Supplier updated successfully.' : 'Supplier created successfully. Use Send Pending Request to invite the supplier to create portal access.'
      });
      setDialogOpen(false);
      loadSuppliers();
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save supplier.' });
    }
  };

  const handleSendInvite = async (supplier) => {
    setInviteLoadingId(supplier._id);
    try {
      const { data } = await api.post(`/suppliers/${supplier._id}/send-invite`);
      setPageMessage({ type: 'success', text: data.message || 'Supplier invitation processed successfully.' });
      if (data.data?.inviteUrl) {
        setInviteDialog({ open: true, link: data.data.inviteUrl, email: data.data.sentTo || '' });
      }
      loadSuppliers();
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to send supplier invitation.' });
    }
    setInviteLoadingId('');
  };

  const copyInviteLink = async () => {
    if (!inviteDialog.link) return;
    try {
      await navigator.clipboard.writeText(inviteDialog.link);
      setPageMessage({ type: 'success', text: 'Invitation link copied to clipboard.' });
    } catch (err) {
      setPageMessage({ type: 'error', text: 'Could not copy the invitation link automatically.' });
    }
  };

  const handleContractUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setContractUploading(true);
    try {
      const uploadForm = new FormData();
      uploadForm.append('document', file);
      const { data } = await api.post('/upload/document', uploadForm, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData((prev) => ({ ...prev, contractFile: data.data.url }));
      setPageMessage({ type: 'success', text: 'Contract file uploaded successfully.' });
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to upload contract file.' });
    }
    setContractUploading(false);
    event.target.value = '';
  };

  const handleDeleteContractFile = () => {
    setFormData((prev) => ({ ...prev, contractFile: '' }));
  };

  const handleAlipayUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAlipayUploading(true);
    try {
      const uploadForm = new FormData();
      uploadForm.append('image', file);
      const { data } = await api.post('/upload/image', uploadForm, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData((prev) => ({ ...prev, alipayQrCode: data.data.url }));
      setPageMessage({ type: 'success', text: 'Alipay QR code uploaded successfully.' });
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to upload Alipay QR code.' });
    }
    setAlipayUploading(false);
    event.target.value = '';
  };

  const handleDeleteAlipayQrCode = () => {
    setFormData((prev) => ({ ...prev, alipayQrCode: '' }));
  };

  const handleWechatQrUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setWechatQrUploading(true);
    try {
      const uploadForm = new FormData();
      uploadForm.append('image', file);
      const { data } = await api.post('/upload/image', uploadForm, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData((prev) => ({ ...prev, wechatQrCode: data.data.url }));
      setPageMessage({ type: 'success', text: 'WeChat QR code uploaded successfully.' });
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to upload WeChat QR code.' });
    }
    setWechatQrUploading(false);
    event.target.value = '';
  };

  const handleDeleteWechatQrCode = () => {
    setFormData((prev) => ({ ...prev, wechatQrCode: '' }));
  };

  const handleStatusChange = async (supplier, status) => {
    setActionLoadingId(`${supplier._id}:${status}`);
    try {
      await api.put(`/suppliers/${supplier._id}/status`, { status });
      setPageMessage({
        type: 'success',
        text: status === 'approved'
          ? 'Supplier approved successfully.'
          : 'Supplier portal access has been frozen successfully.'
      });
      loadSuppliers();
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update supplier status.' });
    }
    setActionLoadingId('');
  };

  const handleDeleteSupplier = async (supplier) => {
    setActionLoadingId(`${supplier._id}:delete`);
    try {
      await api.delete(`/suppliers/${supplier._id}`);
      setPageMessage({ type: 'success', text: 'Supplier and supplier products deleted successfully.' });
      loadSuppliers();
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete supplier.' });
    }
    setActionLoadingId('');
  };

  const openConfirmDialog = (supplier, action) => {
    setConfirmDialog({ open: true, action, supplier });
  };

  const closeConfirmDialog = () => {
    if (actionLoadingId) return;
    setConfirmDialog({ open: false, action: '', supplier: null });
  };

  const handleConfirmAction = async () => {
    const { supplier, action } = confirmDialog;
    if (!supplier || !action) return;

    if (action === 'approved') {
      await handleStatusChange(supplier, 'approved');
    }

    if (action === 'suspended') {
      await handleStatusChange(supplier, 'suspended');
    }

    if (action === 'delete') {
      await handleDeleteSupplier(supplier);
    }

    setConfirmDialog({ open: false, action: '', supplier: null });
  };

  const openEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData({
      companyName: supplier.companyName, companyNameAr: supplier.companyNameAr || '',
      country: supplier.address?.country || '',
      contactName: supplier.contactPerson?.name || '',
      contactPosition: supplier.contactPerson?.position || '',
      contactEmail: supplier.contactPerson?.email || '',
      contactPhone: supplier.contactPerson?.phone || '',
      wechat: supplier.contactPerson?.wechat || '',
      whatsapp: supplier.contactPerson?.whatsapp || '',
      commissionRate: supplier.contractDetails?.commissionRate ?? 10,
      contractFile: supplier.contractDetails?.contractFile || '',
      alipayQrCode: supplier.paymentDetails?.alipayQrCode || '',
      wechatQrCode: supplier.paymentDetails?.wechatQrCode || '',
      bankName: supplier.bankDetails?.bankName || '',
      accountName: supplier.bankDetails?.accountName || '',
      accountNumber: supplier.bankDetails?.accountNumber || '',
      swiftCode: supplier.bankDetails?.swiftCode || '',
      bankCurrency: supplier.bankDetails?.currency || 'CNY',
      language: supplier.language || 'en'
    });
    setDialogOpen(true);
  };

  const openAdd = () => {
    setSelectedSupplier(null);
    setFormData({
      companyName: '', companyNameAr: '', country: '',
      contactName: '', contactPosition: '', contactEmail: '', contactPhone: '',
      wechat: '', whatsapp: '',
      commissionRate: 10, contractFile: '', alipayQrCode: '', wechatQrCode: '',
      bankName: '', accountName: '', accountNumber: '', swiftCode: '', bankCurrency: 'CNY',
      language: 'en'
    });
    setDialogOpen(true);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>{t('manage_suppliers')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add Supplier</Button>
      </Box>

      {pageMessage.text && (
        <Alert severity={pageMessage.type || 'info'} sx={{ mb: 2 }} onClose={() => setPageMessage({ type: '', text: '' })}>
          {pageMessage.text}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Internal Code</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Approved Products</TableCell>
              <TableCell>OSESG Commission</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((supplier) => (
              <TableRow key={supplier._id} hover>
                <TableCell><Chip label={supplier.internalCode} size="small" color="primary" /></TableCell>
                <TableCell>{supplier.companyName}</TableCell>
                <TableCell>{supplier.address?.country}</TableCell>
                <TableCell>{supplier.supplierUsersCount || 0}</TableCell>
                <TableCell>{supplier.approvedProductsCount || 0}</TableCell>
                <TableCell>${(supplier.osesgCommissionReturned || 0).toLocaleString()}</TableCell>
                <TableCell>
                  <Chip label={supplier.status || 'active'} size="small"
                    color={supplier.status === 'approved' || supplier.status === 'active'
                      ? 'success'
                      : supplier.status === 'pending'
                        ? 'warning'
                        : 'default'} />
                </TableCell>
                <TableCell>{supplier.internalRating?.overall?.toFixed(1) || 'N/A'}</TableCell>
                <TableCell>
                  {supplier.status === 'pending' && !supplier.supplierUsersCount && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={inviteLoadingId === supplier._id ? <CircularProgress size={14} /> : <MailOutline />}
                      onClick={() => handleSendInvite(supplier)}
                      disabled={inviteLoadingId === supplier._id || !supplier.contactPerson?.email}
                      sx={{ mr: 1, mb: { xs: 1, md: 0 } }}
                    >
                      Send Pending Request
                    </Button>
                  )}
                  {supplier.status !== 'approved' && (
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => openConfirmDialog(supplier, 'approved')}
                      disabled={actionLoadingId === `${supplier._id}:approved`}
                    >
                      {actionLoadingId === `${supplier._id}:approved` ? <CircularProgress size={18} /> : <CheckCircle />}
                    </IconButton>
                  )}
                  {supplier.status !== 'suspended' && (
                    <IconButton
                      size="small"
                      color="warning"
                      onClick={() => openConfirmDialog(supplier, 'suspended')}
                      disabled={actionLoadingId === `${supplier._id}:suspended`}
                    >
                      {actionLoadingId === `${supplier._id}:suspended` ? <CircularProgress size={18} /> : <Block />}
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => openConfirmDialog(supplier, 'delete')}
                    disabled={actionLoadingId === `${supplier._id}:delete`}
                  >
                    {actionLoadingId === `${supplier._id}:delete` ? <CircularProgress size={18} /> : <Delete />}
                  </IconButton>
                  <IconButton size="small" onClick={() => openEdit(supplier)}><Edit /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedSupplier ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Company Name" value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Company Name (Arabic)" value={formData.companyNameAr}
            onChange={(e) => setFormData({ ...formData, companyNameAr: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Country" value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })} sx={{ mt: 2 }} />
          <TextField select fullWidth label="Language" value={formData.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })} sx={{ mt: 2 }}
            helperText="Preferred language for supplier communication">
            {SUPPORTED_LANGUAGES.map(l => (
              <MenuItem key={l.code} value={l.code}>
                <span style={{ marginRight: 8 }}>{l.flag}</span>
                {l.nativeLabel} ({l.label})
              </MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Contact Name" value={formData.contactName}
            onChange={(e) => setFormData({ ...formData, contactName: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Contact Position" value={formData.contactPosition}
            onChange={(e) => setFormData({ ...formData, contactPosition: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Contact Email" value={formData.contactEmail}
            onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Contact Phone" value={formData.contactPhone}
            onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="WeChat" value={formData.wechat}
            onChange={(e) => setFormData({ ...formData, wechat: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="WhatsApp" value={formData.whatsapp}
            onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="OSESG Commission Rate (%)" type="number" value={formData.commissionRate}
            onChange={(e) => setFormData({ ...formData, commissionRate: Number(e.target.value) })} sx={{ mt: 2 }} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Contract File</Typography>
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={contractUploading ? <CircularProgress size={16} /> : <UploadFile />}
                onClick={() => contractInputRef.current?.click()}
                disabled={contractUploading}
              >
                {formData.contractFile ? 'Update Contract File' : 'Upload Contract File'}
              </Button>
              <input
                ref={contractInputRef}
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={handleContractUpload}
              />
              {formData.contractFile && (
                <>
                  <Button startIcon={<Description />} href={formData.contractFile} target="_blank">View File</Button>
                  <Button color="error" startIcon={<Delete />} onClick={handleDeleteContractFile}>Delete File</Button>
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Payment QR Codes</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <Box sx={{ flex: '1 1 260px', minWidth: 260 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>Alipay QR Code</Typography>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={alipayUploading ? <CircularProgress size={16} /> : <UploadFile />}
                    onClick={() => alipayInputRef.current?.click()}
                    disabled={alipayUploading}
                  >
                    {formData.alipayQrCode ? 'Update QR Code' : 'Upload QR Code'}
                  </Button>
                  <input
                    ref={alipayInputRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleAlipayUpload}
                  />
                  {formData.alipayQrCode && (
                    <Button color="error" startIcon={<Delete />} onClick={handleDeleteAlipayQrCode}>Delete QR Code</Button>
                  )}
                </Box>
                {formData.alipayQrCode && (
                  <Box
                    component="img"
                    src={formData.alipayQrCode}
                    alt="Alipay QR code"
                    sx={{
                      mt: 2,
                      width: 220,
                      maxWidth: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      objectFit: 'contain',
                      bgcolor: '#fff'
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: '1 1 260px', minWidth: 260 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>WeChat QR Code</Typography>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={wechatQrUploading ? <CircularProgress size={16} /> : <UploadFile />}
                    onClick={() => wechatQrInputRef.current?.click()}
                    disabled={wechatQrUploading}
                  >
                    {formData.wechatQrCode ? 'Update QR Code' : 'Upload QR Code'}
                  </Button>
                  <input
                    ref={wechatQrInputRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleWechatQrUpload}
                  />
                  {formData.wechatQrCode && (
                    <Button color="error" startIcon={<Delete />} onClick={handleDeleteWechatQrCode}>Delete QR Code</Button>
                  )}
                </Box>
                {formData.wechatQrCode && (
                  <Box
                    component="img"
                    src={formData.wechatQrCode}
                    alt="WeChat QR code"
                    sx={{
                      mt: 2,
                      width: 220,
                      maxWidth: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      objectFit: 'contain',
                      bgcolor: '#fff'
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Bank Account Details</Typography>
          <TextField fullWidth label="Bank Name" value={formData.bankName}
            onChange={(e) => setFormData({ ...formData, bankName: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Account Name" value={formData.accountName}
            onChange={(e) => setFormData({ ...formData, accountName: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Account Number" value={formData.accountNumber}
            onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="SWIFT Code" value={formData.swiftCode}
            onChange={(e) => setFormData({ ...formData, swiftCode: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Bank Currency" value={formData.bankCurrency}
            onChange={(e) => setFormData({ ...formData, bankCurrency: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteDialog.open} onClose={() => setInviteDialog({ open: false, link: '', email: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Supplier Invitation</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            If SMTP is not configured locally, use this generated link to share the supplier setup page manually.
          </Alert>
          <TextField fullWidth label="Invitation Email" value={inviteDialog.email} InputProps={{ readOnly: true }} sx={{ mt: 1 }} />
          <TextField fullWidth label="Invitation Link" value={inviteDialog.link} InputProps={{ readOnly: true }} sx={{ mt: 2 }} multiline minRows={3} />
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopy />} onClick={copyInviteLink}>Copy Link</Button>
          <Button variant="contained" onClick={() => setInviteDialog({ open: false, link: '', email: '' })}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmDialog.action === 'approved' && 'Approve Supplier'}
          {confirmDialog.action === 'suspended' && 'Freeze Supplier Portal'}
          {confirmDialog.action === 'delete' && 'Delete Supplier'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'approved' && `Approve ${confirmDialog.supplier?.companyName} and allow supplier portal access.`}
            {confirmDialog.action === 'suspended' && `Freeze ${confirmDialog.supplier?.companyName} portal access by disabling supplier user accounts.`}
            {confirmDialog.action === 'delete' && `Delete ${confirmDialog.supplier?.companyName} from the supplier list together with all of this supplier's products and supplier portal users.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmDialog.action === 'delete' ? 'error' : 'primary'}
            onClick={handleConfirmAction}
            disabled={!confirmDialog.supplier}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSuppliers;
